import { useLingui } from "@lingui/react/macro";
import type { DrokToolGroupName } from "@rakazo/contracts";
import { DROK_TOGGLE_GROUP_NAMES } from "@rakazo/contracts";
import { SwitchField } from "@rakazo/ui-web/components/ui/field";

/** Names and one-line explanations of the tool groups, as IT reads them. */
export function useToolGroupInfo(): Record<DrokToolGroupName, { label: string; help: string }> {
  const { t } = useLingui();
  return {
    web: { label: t`Web`, help: t`Search the web and read pages. Employees confirm each use.` },
    computer: {
      label: t`Computer and browser`,
      help: t`A sandbox computer and browser. These actions run without approval.`,
    },
    shell: {
      label: t`Shell and files`,
      help: t`Terminal commands and files on the sandbox. These actions run without approval.`,
    },
    routines: { label: t`Routines`, help: t`Let the agent schedule its own routines.` },
    subagents: { label: t`Helpers`, help: t`Let the agent hand work to helper agents.` },
    media: { label: t`Images and video`, help: t`Generate images and video.` },
    bydsg: {
      label: t`BYDSG meetings`,
      help: t`Manage BYDSG people, projects, meetings, minutes and weekly updates.`,
    },
  };
}

interface ToolGroupFieldsProps {
  value: DrokToolGroupName[];
  onChange: (next: DrokToolGroupName[]) => void;
  disabled?: boolean;
}

export function ToolGroupFields({ value, onChange, disabled }: ToolGroupFieldsProps) {
  const info = useToolGroupInfo();
  return (
    <div className="space-y-3">
      {DROK_TOGGLE_GROUP_NAMES.map((name) => (
        <SwitchField
          key={name}
          data-testid={`group-${name}`}
          label={info[name].label}
          description={info[name].help}
          disabled={disabled}
          checked={value.includes(name)}
          onCheckedChange={(on) =>
            onChange(on ? [...value, name] : value.filter((group) => group !== name))
          }
        />
      ))}
    </div>
  );
}
